import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AdminShell from "@/components/admin/AdminShell";
import api, { formatApiError, API } from "@/lib/api";
import { TRIBES } from "@/lib/tribes";
import { ArrowLeft, CheckCircle2, Clock, Lock, Flag, Heart, ClipboardList, Award, User, Mail, Calendar, Download, StickyNote, Trash2, PlusCircle, Sparkles, Eye, Wallet } from "lucide-react";

function ResetPasswordButton({ parentId, email, onDone }) {
  const [saving, setSaving] = useState(false);
  const [temp, setTemp] = useState("");
  if (!parentId) return null;
  const run = async () => {
    if (!window.confirm(`Reset the password for ${email}?\n\nA new temporary password will be generated. Share it with the customer — they can log in and change it in their account.`)) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/parent/${parentId}/reset-password`, {});
      setTemp(data?.temp_password || "");
      onDone?.();
    } catch (e) { alert(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(temp); alert("Copied to clipboard."); } catch (_) {}
  };
  return (
    <>
      <button
        onClick={run}
        disabled={saving}
        data-testid="admin-reset-password"
        className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full font-semibold disabled:opacity-50 bg-sky-500/15 text-sky-200 border border-sky-400/40 hover:bg-sky-500/25"
        title="Generate a new temporary password for this parent"
      >
        <Lock size={14} /> {saving ? "Resetting…" : "Reset password"}
      </button>
      {temp && (
        <div data-testid="admin-reset-password-result" className="mt-3 rounded-xl border border-sky-400/40 bg-sky-500/10 p-4 text-sm text-slate-100 max-w-lg">
          <div className="font-semibold mb-1">Temporary password (shown once):</div>
          <div className="flex items-center gap-2">
            <code className="font-mono text-base bg-black/30 px-2 py-1 rounded">{temp}</code>
            <button onClick={copy} className="text-xs px-2 py-1 rounded bg-white/10 hover:bg-white/20">Copy</button>
          </div>
          <div className="text-xs text-slate-400 mt-2">Share this with <strong>{email}</strong> — they can sign in and change it under Account → Password.</div>
        </div>
      )}
    </>
  );
}

function AccessStatusToggle({ parentId, current, onChanged }) {
  const [saving, setSaving] = useState(false);
  if (!parentId) return null;
  const isPreview = current === "preview";
  const target = isPreview ? "full" : "preview";
  const flip = async () => {
    const label = isPreview ? "grant FULL access to this parent" : "downgrade this parent back to FREE PREVIEW";
    if (!window.confirm(`Are you sure you want to ${label}?`)) return;
    setSaving(true);
    try {
      await api.post(`/admin/parent/${parentId}/access-status`, { status: target });
      onChanged?.();
    } catch (e) { alert(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <button
      onClick={flip}
      disabled={saving}
      data-testid={`admin-access-toggle-${target}`}
      className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full font-semibold disabled:opacity-50 ${
        isPreview
          ? "bg-emerald-500/20 text-emerald-200 border border-emerald-400/40 hover:bg-emerald-500/30"
          : "bg-amber-500/15 text-amber-200 border border-amber-400/30 hover:bg-amber-500/25"
      }`}
    >
      {isPreview ? <><Sparkles size={14} /> {saving ? "Granting…" : "Grant full access"}</> : <><Eye size={14} /> {saving ? "Setting…" : "Downgrade to preview"}</>}
    </button>
  );
}

function PaidSlotsOverride({ parentId, current, enrolled, onChanged }) {
  const [saving, setSaving] = useState(false);
  if (!parentId) return null;
  const allowed = 1 + current; // 1 free + add-ons
  const mismatch = enrolled > allowed;
  const apply = async () => {
    const raw = window.prompt(
      `Add-on slots (extra family members beyond the first free child).\n\n` +
      `Currently: ${current} add-on slot(s) purchased · ${enrolled} enrolled · ${allowed} total allowed.\n` +
      `Enter a new value 0-50 (each unit = one additional sibling this family can enroll):`,
      String(current)
    );
    if (raw === null) return;
    const n = parseInt(raw, 10);
    if (Number.isNaN(n) || n < 0 || n > 50) { alert("Please enter a whole number between 0 and 50."); return; }
    if (n === current) return;
    setSaving(true);
    try {
      await api.post(`/admin/parent/${parentId}/paid-slots`, { paid_slots: n });
      onChanged?.();
    } catch (e) { alert(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <button
      onClick={apply}
      disabled={saving}
      data-testid="admin-paid-slots-override"
      className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full font-semibold disabled:opacity-50 border ${
        mismatch
          ? "bg-rose-500/15 text-rose-200 border-rose-400/40 hover:bg-rose-500/25"
          : "bg-slate-500/15 text-slate-200 border-slate-400/30 hover:bg-slate-500/25"
      }`}
      title={mismatch ? "Enrolled count exceeds allowed — needs attention" : `${enrolled} enrolled · ${current} add-on slots purchased · ${allowed} total allowed`}
    >
      <Wallet size={14} /> {saving ? "Saving…" : `Slots: ${enrolled}/${allowed}`}
    </button>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

const SectionCard = ({ title, count, icon: Icon, children, testid }) => (
  <section className="rounded-2xl bg-[#151737] border border-white/10 p-5 md:p-6" data-testid={testid}>
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        {Icon && <Icon size={16} className="text-amber-300" />}
        <h2 className="font-display text-lg font-bold text-white">{title}</h2>
      </div>
      {count !== undefined && <span className="text-[11px] font-semibold text-slate-300 bg-white/10 px-2 py-1 rounded-full">{count}</span>}
    </div>
    {children}
  </section>
);

const StatusPill = ({ status }) => {
  const map = {
    complete: { label: "Complete", cls: "bg-emerald-500/15 text-emerald-300", Icon: CheckCircle2 },
    in_progress: { label: "In progress", cls: "bg-amber-400/15 text-amber-300", Icon: Clock },
    locked: { label: "Locked", cls: "bg-slate-500/15 text-slate-400", Icon: Lock },
  };
  const m = map[status] || map.locked;
  const Icon = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${m.cls}`}>
      <Icon size={10} /> {m.label}
    </span>
  );
};

function RoadblockStatus({ status }) {
  const map = {
    none: { label: "No concern", cls: "bg-slate-500/15 text-slate-400" },
    flagged: { label: "Flagged", cls: "bg-rose-400/15 text-rose-300" },
    referred: { label: "Referred", cls: "bg-amber-400/15 text-amber-300" },
    cleared: { label: "Cleared", cls: "bg-emerald-400/15 text-emerald-300" },
  };
  const m = map[status] || map.none;
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${m.cls}`}>{m.label}</span>;
}

// Render one Myo Check-In with all answers expanded per section.
function CheckInEntry({ ci }) {
  const [open, setOpen] = useState(false);
  const groups = [
    ["Breathing", ci.breathing],
    ["Lips", ci.lips],
    ["Tongue", ci.tongue],
    ["Congestion", ci.congestion],
    ["Swallow", ci.swallow],
  ];
  return (
    <div className="rounded-xl border border-white/10 bg-[#0F1020]" data-testid={`admin-checkin-${ci.id}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`admin-checkin-toggle-${ci.id}`}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-white/5 rounded-xl"
      >
        <div className="flex items-center gap-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Session {ci.session_number}</div>
          {ci.is_draft && <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-400/15 text-amber-300">DRAFT</span>}
          <div className="text-sm text-slate-200">{fmt(ci.created_at)}</div>
        </div>
        <div className="text-xs text-slate-400">{open ? "Hide" : "Show"}</div>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3">
          {groups.map(([title, data]) => (
            <div key={title} className="rounded-lg bg-[#151737] border border-white/5 p-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-semibold mb-1.5">{title}</div>
              {data && Object.keys(data).length > 0 ? (
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  {Object.entries(data).map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-2 border-b border-white/5 py-1">
                      <dt className="text-slate-400">{k}</dt>
                      <dd className="text-slate-100 font-medium text-right">{String(v)}</dd>
                    </div>
                  ))}
                </dl>
              ) : <div className="text-xs text-slate-500 italic">No entries</div>}
            </div>
          ))}
          {(ci.top_symptoms || ci.other_notes || ci.struggles || ci.wins) && (
            <div className="rounded-lg bg-[#151737] border border-white/5 p-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-semibold mb-1.5">Reflection</div>
              {ci.other_notes && <div className="text-xs text-slate-200 mb-1" data-testid={`admin-checkin-other-notes-${ci.id}`}><span className="text-slate-400">Other notes:</span> {ci.other_notes}</div>}
              {ci.top_symptoms && <div className="text-xs text-slate-200 mb-1"><span className="text-slate-400">Top symptoms (legacy):</span> {ci.top_symptoms}</div>}
              {ci.struggles && <div className="text-xs text-slate-200 mb-1"><span className="text-slate-400">Struggles:</span> {ci.struggles}</div>}
              {ci.wins && <div className="text-xs text-slate-200"><span className="text-slate-400">Wins:</span> {ci.wins}</div>}
            </div>
          )}
          {ci.symptom_updates && Object.keys(ci.symptom_updates).length > 0 && (
            <div className="rounded-lg bg-[#151737] border border-white/5 p-3" data-testid={`admin-checkin-symptom-updates-${ci.id}`}>
              <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-semibold mb-1.5">Per-symptom notes</div>
              <div className="space-y-1.5">
                {Object.entries(ci.symptom_updates).map(([sKey, upd]) => (
                  <div key={sKey} className="text-xs text-slate-200 flex flex-col gap-0.5 border-b border-white/5 pb-1.5 last:border-0 last:pb-0">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400">{sKey}</span>
                      {upd?.rating && (
                        <span
                          className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                            upd.rating === "better"
                              ? "bg-emerald-500/15 text-emerald-200"
                              : upd.rating === "worse"
                              ? "bg-rose-500/15 text-rose-200"
                              : upd.rating === "resolved"
                              ? "bg-amber-500/15 text-amber-200"
                              : "bg-white/10 text-slate-200"
                          }`}
                        >
                          {upd.rating}
                        </span>
                      )}
                    </div>
                    {upd?.detail && (
                      <div className="text-slate-100 pl-1 italic leading-relaxed whitespace-pre-line">
                        "{upd.detail}"
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RefundNotesCard({ notes, childId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [verdict, setVerdict] = useState("pending");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSaving(true);
    try {
      const body = { verdict, note };
      const amt = parseFloat(amount);
      if (!Number.isNaN(amt)) body.amount = amt;
      await api.post(`/admin/child/${childId}/refund-notes`, body);
      setVerdict("pending"); setAmount(""); setNote(""); setOpen(false);
      onChanged && onChanged();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this refund note? This is logged in the activity trail.")) return;
    try {
      await api.delete(`/admin/refund-notes/${id}`);
      onChanged && onChanged();
    } catch (e) { alert(formatApiError(e)); }
  };

  const VERDICT_META = {
    pending: { label: "Pending", cls: "bg-slate-500/15 text-slate-300" },
    granted_full: { label: "Granted (full)", cls: "bg-emerald-500/15 text-emerald-300" },
    granted_partial: { label: "Granted (partial)", cls: "bg-amber-400/15 text-amber-300" },
    denied: { label: "Denied", cls: "bg-rose-400/15 text-rose-300" },
  };

  return (
    <section className="rounded-2xl bg-[#151737] border border-white/10 p-5 md:p-6" data-testid="admin-audit-refund-notes">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <StickyNote size={16} className="text-amber-300" />
          <h2 className="font-display text-lg font-bold text-white">Refund verdict notes</h2>
          <span className="text-[11px] font-semibold text-slate-300 bg-white/10 px-2 py-1 rounded-full">{notes.length}</span>
        </div>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          data-testid="admin-refund-add-toggle"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0F1020] bg-gradient-to-r from-amber-400 to-rose-400 px-3 py-1.5 rounded-full hover:opacity-90"
        >
          <PlusCircle size={13} /> {open ? "Cancel" : "Pin a decision"}
        </button>
      </div>

      {open && (
        <form onSubmit={submit} className="mb-4 rounded-xl bg-[#0F1020] border border-white/10 p-4 space-y-3" data-testid="admin-refund-form">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-semibold">Verdict</label>
              <select data-testid="admin-refund-verdict" value={verdict} onChange={(e) => setVerdict(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg bg-[#151737] border border-white/10 focus:border-amber-300 focus:outline-none text-slate-100 text-sm">
                {Object.entries(VERDICT_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-semibold">Amount ($, optional)</label>
              <input data-testid="admin-refund-amount" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" placeholder="e.g. 60" className="mt-1 w-full px-3 py-2 rounded-lg bg-[#151737] border border-white/10 focus:border-amber-300 focus:outline-none text-slate-100 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-semibold">Reasoning / private note</label>
            <textarea data-testid="admin-refund-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="e.g. Completed 4/12 sessions. Refunding 60% per policy." className="mt-1 w-full px-3 py-2 rounded-lg bg-[#151737] border border-white/10 focus:border-amber-300 focus:outline-none text-slate-100 text-sm" />
          </div>
          {err && <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-2 py-1.5" data-testid="admin-refund-error">{err}</div>}
          <div className="flex justify-end">
            <button data-testid="admin-refund-submit" disabled={saving} className="bg-gradient-to-r from-amber-400 to-rose-400 text-[#0F1020] px-4 py-2 rounded-full text-sm font-semibold hover:opacity-90 disabled:opacity-50">
              {saving ? "Saving…" : "Pin decision"}
            </button>
          </div>
        </form>
      )}

      {notes.length === 0 && !open && <div className="text-slate-500 text-sm italic">No verdicts pinned to this family yet.</div>}
      <ul className="space-y-2">
        {notes.map((n) => {
          const m = VERDICT_META[n.verdict] || VERDICT_META.pending;
          return (
            <li key={n.id} className="rounded-lg bg-[#0F1020] border border-white/5 px-3 py-2.5" data-testid={`admin-refund-note-${n.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${m.cls}`}>{m.label}</span>
                    {n.amount !== null && n.amount !== undefined && n.amount !== "" && (
                      <span className="text-xs text-slate-200 font-semibold">${Number(n.amount).toFixed(2)}</span>
                    )}
                    <span className="text-[10px] text-slate-500">{fmt(n.created_at)}</span>
                  </div>
                  {n.note && <div className="text-sm text-slate-200 mt-1.5 whitespace-pre-wrap">{n.note}</div>}
                  <div className="text-[10px] text-slate-500 mt-1">by {n.admin_email}</div>
                </div>
                <button type="button" onClick={() => remove(n.id)} data-testid={`admin-refund-note-delete-${n.id}`} className="text-slate-500 hover:text-rose-300 shrink-0" title="Delete">
                  <Trash2 size={14} />
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default function AdminChildAudit() {
  const { childId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = () => {
    api.get(`/admin/child/${childId}/audit`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load audit"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [childId]);

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem("btt_token");
      const res = await fetch(`${API}/admin/child/${childId}/audit.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to download CSV");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = (res.headers.get("content-disposition") || "").match(/filename="?([^";]+)"?/)?.[1] || `audit-${childId}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { alert(e.message || "CSV download failed"); }
  };

  const resetFamily = async () => {
    if (!data?.parent?.id) return;
    const parentEmail = data.parent.email;
    const confirmA = window.prompt(`This wipes EVERY child and all of their data for ${parentEmail}. The parent account itself is kept so they can log back in.\n\nType RESET to confirm.`);
    if (confirmA !== "RESET") return;
    try {
      const { data: res } = await api.post(`/admin/parent/${data.parent.id}/reset-family`);
      alert(`Family reset complete.\nDeleted: ${res.deleted.children} child(ren), ${res.deleted.checkins} check-in(s), ${res.deleted.roadblocks} roadblock(s), ${res.deleted.certificates} certificate(s), ${res.deleted.exercise_logs} exercise log(s).\n\nThe parent can now log in and start over from step 1.`);
      // Navigate back — this child no longer exists.
      window.location.href = "/admin/families";
    } catch (e) { alert(formatApiError(e)); }
  };

  if (err) return <AdminShell title="Audit" subtitle="Family lookup"><div className="text-rose-300" data-testid="admin-audit-error">{err}</div></AdminShell>;
  if (!data) return <AdminShell title="Loading…" subtitle="Family lookup"><div className="text-slate-400">Loading audit…</div></AdminShell>;

  const { child, parent, sessions, checkins, roadblocks, certificates, donation_total, refund_notes = [], checkin_reminder_acks = [] } = data;
  const tribe = TRIBES[child.tribe];
  const completedCount = sessions.filter((s) => s.status === "complete").length;

  return (
    <AdminShell
      subtitle="Family lookup"
      title={child.name}
      right={
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={downloadCsv} data-testid="admin-audit-csv-btn" className="inline-flex items-center gap-2 text-sm text-[#0F1020] bg-gradient-to-r from-amber-400 to-rose-400 px-4 py-2 rounded-full font-semibold hover:opacity-90">
            <Download size={14} /> Download CSV
          </button>
          <button onClick={resetFamily} data-testid="admin-audit-reset-btn" className="inline-flex items-center gap-2 text-sm text-rose-300 border border-rose-400/40 hover:bg-rose-500/10 px-4 py-2 rounded-full font-semibold">
            <Trash2 size={14} /> Reset family data
          </button>
          <AccessStatusToggle parentId={data?.parent?.id} current={data?.parent?.access_status || "full"} onChanged={load} />
          <PaidSlotsOverride parentId={data?.parent?.id} current={data?.parent?.paid_slots || 0} enrolled={data?.parent?.enrolled_children_count || 0} onChanged={load} />
          <Link to="/admin/families" data-testid="admin-audit-back" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white px-3 py-2">
            <ArrowLeft size={14} /> Back
          </Link>
        </div>
      }
    >
      {/* Header summary */}
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <div className="rounded-2xl bg-[#151737] border border-white/10 p-5" data-testid="admin-audit-summary-progress">
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-semibold">Program progress</div>
          <div className="mt-2 font-display text-3xl font-bold text-white">{completedCount}<span className="text-lg text-slate-500">/12</span></div>
          <div className="text-xs text-slate-400 mt-1">Sessions marked complete</div>
        </div>
        <div className="rounded-2xl bg-[#151737] border border-white/10 p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-semibold">Current session</div>
          <div className="mt-2 font-display text-3xl font-bold text-white">{child.current_session}</div>
          <div className="text-xs text-slate-400 mt-1">Unlocks next: {fmtDate(child.session_unlocks_at)}</div>
        </div>
        <div className="rounded-2xl bg-[#151737] border border-white/10 p-5" data-testid="admin-audit-summary-donations">
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-semibold">Donations from this child</div>
          <div className="mt-2 font-display text-3xl font-bold text-white">${donation_total}</div>
          <div className="text-xs text-slate-400 mt-1">$1 credited per session with submitted proof photo</div>
        </div>
        <div className="rounded-2xl bg-[#151737] border border-white/10 p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-semibold">Enrolled</div>
          <div className="mt-2 font-display text-xl font-bold text-white">{fmtDate(child.enrolled_at)}</div>
          <div className="text-xs text-slate-400 mt-1">Tribe: {tribe?.name || child.tribe}</div>
        </div>
      </div>

      {/* Refund verdict notes — top-of-page so it's the first thing an admin sees when triaging a refund. */}
      <div className="mb-6">
        <RefundNotesCard notes={refund_notes} childId={childId} onChanged={load} />
      </div>

      {/* Enrollment + parent */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <SectionCard title="Parent account" icon={User} testid="admin-audit-parent">
          {parent ? (
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-slate-100"><User size={12} className="text-slate-500" /> {parent.name || "—"}</div>
              <div className="flex items-center gap-2 text-slate-100"><Mail size={12} className="text-slate-500" /> {parent.email}</div>
              <div className="flex items-center gap-2 text-slate-300"><Calendar size={12} className="text-slate-500" /> Account created {fmtDate(parent.created_at)}</div>
              <div className="text-slate-300 text-xs">Total donations across all children: ${parent.donations_given}</div>
              <div className="text-slate-300 text-xs">Education completed: {parent.education_completed_at ? fmtDate(parent.education_completed_at) : <span className="text-rose-300">Not yet</span>}</div>
            </div>
          ) : <div className="text-slate-500 text-sm">No parent record on file</div>}
        </SectionCard>
        <SectionCard title="Enrollment details" icon={ClipboardList} testid="admin-audit-enrollment">
          <div className="space-y-2 text-sm">
            <div className="text-slate-300">Age at enrollment: <span className="text-white font-semibold">{child.age}</span></div>
            <div className="text-slate-300">Tribe: <span className="text-white font-semibold">{tribe?.name || child.tribe}</span></div>
            <div className="text-slate-300">Appliance in use: <span className="text-white font-semibold">{child.appliance_in_use ? "Yes" : "No"}</span></div>
            <div className="text-slate-300">Top symptoms at enrollment:</div>
            <div className="flex flex-wrap gap-1.5">
              {(child.top_symptoms || []).length === 0 && <span className="text-xs text-slate-500 italic">None selected</span>}
              {(child.top_symptoms || []).map((s) => (
                <span key={s} className="px-2 py-0.5 rounded-full text-[11px] bg-white/5 border border-white/10 text-slate-200">{s}</span>
              ))}
            </div>
          </div>
        </SectionCard>
      </div>

      {/* Sessions timeline */}
      <div className="mb-6">
        <SectionCard title="Session-by-session" count={`${completedCount}/12 complete`} icon={CheckCircle2} testid="admin-audit-sessions">
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold border-b border-white/5">
                  <th className="text-left py-2 px-2">#</th>
                  <th className="text-left px-2">Status</th>
                  <th className="text-left px-2">Started</th>
                  <th className="text-left px-2">Completed</th>
                  <th className="text-right px-2">Practices logged</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.session_number} className="border-b border-white/5 last:border-0" data-testid={`admin-audit-session-${s.session_number}`}>
                    <td className="py-2 px-2 font-display font-bold text-white">{s.session_number}</td>
                    <td className="px-2"><StatusPill status={s.status} /></td>
                    <td className="px-2 text-slate-300 text-xs">{s.started_at ? fmt(s.started_at) : "—"}</td>
                    <td className="px-2 text-slate-300 text-xs">{s.completed_at ? fmt(s.completed_at) : "—"}</td>
                    <td className="px-2 text-right text-slate-200 font-semibold" data-testid={`admin-audit-practices-${s.session_number}`}>{s.practice_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      </div>

      {/* Check-ins full history */}
      <div className="mb-6">
        <SectionCard title="Myo Check-In history" count={checkins.length} icon={ClipboardList} testid="admin-audit-checkins">
          {checkins.length === 0 && <div className="text-slate-500 text-sm italic">No check-ins submitted yet.</div>}
          <div className="space-y-2">
            {checkins.map((ci) => <CheckInEntry key={ci.id} ci={ci} />)}
          </div>
        </SectionCard>
      </div>

      {/* Myo Check-In reminder acknowledgments — refund/dispute audit trail. Shows
          every time we showed the one-time popup, which button the parent tapped,
          and when. Independent of whether the check-in was actually completed. */}
      <div className="mb-6">
        <SectionCard
          title="Myo Check-In reminder acknowledgments"
          count={checkin_reminder_acks.length}
          icon={Flag}
          testid="admin-audit-checkin-acks"
        >
          <div className="text-[11px] text-slate-500 mb-3 leading-relaxed">
            Timestamped record of the one-time popup shown at the start of each session. Proves the parent was
            notified about the Myo Check-In, independent of whether they actually completed it afterwards.
          </div>
          {checkin_reminder_acks.length === 0 ? (
            <div className="text-slate-500 text-sm italic">No acknowledgments recorded yet.</div>
          ) : (
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold border-b border-white/5">
                    <th className="text-left py-2 px-2">Session #</th>
                    <th className="text-left px-2">Notified at</th>
                    <th className="text-left px-2">Parent choice</th>
                  </tr>
                </thead>
                <tbody>
                  {checkin_reminder_acks.map((ack) => (
                    <tr
                      key={ack.id}
                      className="border-b border-white/5 last:border-0"
                      data-testid={`admin-audit-checkin-ack-${ack.session_number}`}
                    >
                      <td className="py-2 px-2 font-display font-bold text-white">{ack.session_number}</td>
                      <td className="px-2 text-slate-300 text-xs">{fmt(ack.created_at)}</td>
                      <td className="px-2">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                            ack.action === "fill_now"
                              ? "bg-emerald-500/15 text-emerald-200 border border-emerald-400/30"
                              : "bg-amber-500/15 text-amber-200 border border-amber-400/30"
                          }`}
                        >
                          {ack.action === "fill_now" ? "Fill it out now" : "I'll do it later"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      </div>

      {/* Certificates + Roadblocks side by side */}
      <div className="grid md:grid-cols-2 gap-4">
        <SectionCard title="Certificates &amp; donations" count={`${certificates.length} cert · $${donation_total} donated`} icon={Award} testid="admin-audit-certificates">
          {certificates.length === 0 && <div className="text-slate-500 text-sm italic">No certificates earned yet.</div>}
          <ul className="space-y-2">
            {certificates.map((c) => (
              <li key={c.id} className="rounded-lg bg-[#0F1020] border border-white/5 px-3 py-2 flex items-center justify-between" data-testid={`admin-audit-cert-${c.session_number}`}>
                <div>
                  <div className="text-sm text-white font-semibold">Session {c.session_number}</div>
                  <div className="text-xs text-slate-400">Earned {fmt(c.date)}</div>
                </div>
                <div className="text-right">
                  {c.photo_path ? (
                    <span className="inline-flex items-center gap-1 text-emerald-300 text-xs font-semibold"><Heart size={11} /> Proof · $1 credited</span>
                  ) : (
                    <span className="text-xs text-slate-500 italic">No proof photo yet</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </SectionCard>
        <SectionCard title="Structural roadblocks" count={roadblocks.length} icon={Flag} testid="admin-audit-roadblocks">
          {roadblocks.length === 0 && <div className="text-slate-500 text-sm italic">No roadblocks recorded.</div>}
          <ul className="space-y-2">
            {roadblocks.map((r) => (
              <li key={r.id} className="rounded-lg bg-[#0F1020] border border-white/5 px-3 py-2" data-testid={`admin-audit-rb-${r.kind}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm text-white font-semibold capitalize">{String(r.kind).replace(/_/g, " ")}</div>
                  <RoadblockStatus status={r.status} />
                </div>
                {r.severity !== null && r.severity !== undefined && (
                  <div className="text-xs text-slate-400 mt-0.5">Tonsil grade: <span className="text-slate-200 font-semibold">{r.severity}</span></div>
                )}
                {r.note && <div className="text-xs text-slate-300 mt-1 italic">"{r.note}"</div>}
                {r.photo_path && <div className="text-[10px] text-slate-500 mt-1">Photo/scan on file</div>}
              </li>
            ))}
          </ul>
        </SectionCard>
      </div>
    </AdminShell>
  );
}
